import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Select } from '../../components/ui/form/Select';
import { usePlayer } from '../../stores/player';
import { useSession } from '../../stores/session';
import { Artwork } from './LibraryDock';
import { playlists, type Playlist } from './api';

/**
 * One playlist, inside the library panel.
 *
 * ## Reordering happens two ways, and that is on purpose
 *
 * Dragging is what people expect of a playlist, so rows drag. But the album
 * editor already reorders with arrow buttons and says why in a comment worth
 * keeping: dragging needs a pointer, and a keyboard or touch user gets nothing
 * from it. So the arrows are here too, doing the same job for anybody the drag
 * does not serve, rather than being replaced by it.
 *
 * Both send the whole running order. The server takes the complete list because
 * a relative "move 3 to 7" has to be interpreted against a state the client may
 * no longer share with it.
 *
 * ## Renaming is the playlist's title only
 *
 * Never a track's. A playlist is mostly other people's music and their titles
 * are theirs - the whole reason `playlistTrack` exists on the server instead of
 * relaxing the ownership rule.
 */
export function PlaylistTracks({
  playlistId,
  onBack,
  onClose,
}: {
  playlistId: number;
  onBack: () => void;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const user = useSession((state) => state.user);
  const play = usePlayer((state) => state.play);

  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');
  const [dragging, setDragging] = useState<number | null>(null);

  const key = ['playlist', playlistId];

  const { data, isPending } = useQuery({
    queryKey: key,
    queryFn: () => playlists.get(playlistId),
  });

  const refresh = () => {
    void client.invalidateQueries({ queryKey: key });
    void client.invalidateQueries({ queryKey: ['playlists', user?.username] });
  };

  const reorder = useMutation({
    mutationFn: (trackIds: number[]) => playlists.reorder(playlistId, trackIds),
    onSuccess: refresh,
  });

  const drop = useMutation({
    mutationFn: (trackId: number) => playlists.removeTrack(playlistId, trackId),
    onSuccess: refresh,
  });

  const rename = useMutation({
    mutationFn: (title: string) => playlists.rename(playlistId, title),
    onSuccess: () => {
      setRenaming(false);
      refresh();
    },
  });

  const visibility = useMutation({
    mutationFn: (next: Playlist['visibility']) => playlists.setVisibility(playlistId, next),
    onSuccess: refresh,
  });

  const destroy = useMutation({
    mutationFn: () => playlists.remove(playlistId),
    onSuccess: () => {
      refresh();
      onBack();
    },
  });

  const tracks = data?.tracks ?? [];

  /*
   * What "play this playlist" actually plays.
   *
   * A playlist is mostly other people's music, and a track can stop being
   * playable after it was added - taken down, or made private by its artist.
   * Handing those to the player would be a queue that stalls partway through
   * on a row nobody can hear.
   */
  const playable = tracks.filter((track) => track.status === 'ready');

  /** Move by however many places, then send the whole order. */
  const move = (index: number, to: number) => {
    if (to < 0 || to >= tracks.length || to === index) return;

    const next = [...tracks];
    const [lifted] = next.splice(index, 1);

    if (!lifted) return;

    next.splice(to, 0, lifted);

    reorder.mutate(next.map((track) => track.id));
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-1.5 border-b border-line px-2 py-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to playlists"
          data-playlist-back
          className="shrink-0 rounded px-1.5 py-1 font-mono text-[13px] text-soft transition-colors hover:text-ink"
        >
          ‹
        </button>

        {renaming ? (
          <form
            className="flex min-w-0 flex-1 items-center gap-1.5"
            onSubmit={(event) => {
              event.preventDefault();

              const title = draft.trim();

              if (title !== '') rename.mutate(title);
            }}
          >
            <input
              autoFocus
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.stopPropagation();
                  setRenaming(false);
                }
              }}
              aria-label="Playlist name"
              data-playlist-rename-input
              maxLength={120}
              className="min-w-0 flex-1 rounded-[var(--radius-pill)] border border-line bg-app px-2.5 py-1 text-[13px] text-ink outline-none focus:border-line-strong"
            />
            <button
              type="submit"
              className="meta-caps shrink-0 rounded-[var(--radius-pill)] bg-accent px-2.5 py-1 text-accent-ink"
            >
              Save
            </button>
          </form>
        ) : (
          <>
            <span
              className="min-w-0 flex-1 truncate text-sm font-medium text-ink"
              data-playlist-title
            >
              {data?.title ?? 'Playlist'}
            </span>

            {/*
              The primary action, so it gets the primary slot.

              Playing the thing you opened is what somebody came here to do;
              renaming it is housekeeping, and sits with the other housekeeping
              on the row below.
            */}
            <button
              type="button"
              disabled={playable.length === 0}
              onClick={() => {
                const first = playable[0];

                if (!first) return;

                void play(first, playable, 'collection');
                onClose();
              }}
              aria-label={`Play ${data?.title ?? 'playlist'}`}
              data-playlist-play-all
              className="flex shrink-0 items-center gap-1.5 rounded-[var(--radius-pill)] bg-accent px-3 py-1 text-accent-ink transition-opacity hover:opacity-90 disabled:opacity-30"
            >
              <svg viewBox="0 0 16 16" className="h-3 w-3" fill="currentColor" aria-hidden>
                <path d="M4 3l9 5-9 5z" />
              </svg>
              <span className="meta-caps">Play</span>
            </button>
          </>
        )}
      </div>

      {/*
        Visibility, per playlist.

        Offered here rather than only at creation because the interesting moment
        is later: a list made privately in one second is the one somebody
        decides to share a fortnight afterwards.
      */}
      {data && (
        <div className="flex shrink-0 items-center gap-2 border-b border-line px-2.5 py-1.5">
          {/*
            Not `compact`.
 
            That flag means chevron-only - for controls sitting inside
            something that has already named them - and using it here rendered
            the visibility of a playlist as a bare arrow with nothing to say
            what it was set to. Which is exactly the setting somebody needs to
            be able to read at a glance.
          */}
          <Select
            className="w-[150px]"
            value={data.visibility}
            aria-label="Who can see this playlist"
            onChange={(next) => visibility.mutate(next as Playlist['visibility'])}
            options={[
              { value: 'private', label: 'Only me' },
              { value: 'unlisted', label: 'Anyone with the link' },
              { value: 'public', label: 'Everyone' },
            ]}
          />

          <span className="flex-1" />

          <button
            type="button"
            onClick={() => {
              setDraft(data.title);
              setRenaming(true);
            }}
            aria-label="Rename playlist"
            data-playlist-rename
            className="shrink-0 rounded px-1.5 py-1 text-[11px] text-soft transition-colors hover:text-ink"
          >
            Rename
          </button>

          <button
            type="button"
            onClick={() => destroy.mutate()}
            disabled={destroy.isPending}
            data-playlist-delete
            className="shrink-0 rounded px-1.5 py-1 text-[11px] text-soft transition-colors hover:text-danger disabled:opacity-40"
          >
            Delete
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
        {isPending && <p className="px-2 py-3 font-mono text-[10px] text-ghost">loading…</p>}

        {!isPending && tracks.length === 0 && (
          <p className="px-2 py-3 text-xs text-faint">
            Empty. Play something and use the + in the bar to put it here.
          </p>
        )}

        {tracks.map((track, index) => (
          <div
            key={track.id}
            draggable
            data-playlist-row={track.id}
            onDragStart={() => setDragging(index)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();

              if (dragging !== null) move(dragging, index);

              setDragging(null);
            }}
            onDragEnd={() => setDragging(null)}
            className={`flex items-center gap-1.5 rounded-[var(--radius-control)] px-1.5 py-1.5 transition-colors hover:bg-slate ${
              dragging === index ? 'opacity-40' : ''
            }`}
          >
            <span
              aria-hidden
              className="shrink-0 cursor-grab font-mono text-[11px] text-ghost"
              title="Drag to reorder"
            >
              ⠿
            </span>

            <button
              type="button"
              onClick={() => {
                void play(track, tracks, 'collection');
                onClose();
              }}
              data-playlist-play={track.id}
              aria-label={`Play ${track.title}`}
              className="flex min-w-0 flex-1 items-center gap-2 text-left"
            >
              <Artwork track={track} />

              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] text-ink">{track.title}</span>
                <span className="block truncate text-[11px] text-faint">
                  {track.owner?.display_name ?? track.owner?.username ?? ''}
                </span>
              </span>
            </button>

            {/*
              The keyboard's way to do what the drag does. See the note at the
              top - this is not a leftover, it is the accessible half.
            */}
            <span className="flex shrink-0 flex-col">
              <button
                type="button"
                onClick={() => move(index, index - 1)}
                disabled={index === 0}
                aria-label={`Move ${track.title} up`}
                data-playlist-up={track.id}
                className="px-1 font-mono text-[9px] leading-none text-soft transition-colors hover:text-ink disabled:opacity-25"
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => move(index, index + 1)}
                disabled={index === tracks.length - 1}
                aria-label={`Move ${track.title} down`}
                data-playlist-down={track.id}
                className="px-1 font-mono text-[9px] leading-none text-soft transition-colors hover:text-ink disabled:opacity-25"
              >
                ▼
              </button>
            </span>

            <button
              type="button"
              onClick={() => drop.mutate(track.id)}
              aria-label={`Remove ${track.title}`}
              data-playlist-remove={track.id}
              className="shrink-0 rounded px-1 py-1 font-mono text-[11px] text-soft transition-colors hover:text-danger"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
